import type { Product, ProductVariantsResponse } from '@bbpos/types';
import type { VariantAttribute, VariantOption } from '@bbpos/ui/src/components/POSBillingComponents/VariantMatrix/types';

/**
 * Pure helpers for the POS variant picker.
 *
 * `GET /products/:id/variants` already returns the attribute groups (ordered
 * by the tenant's attribute library) and the child rows; the picker needs the
 * `VariantOption` shape, and the cart needs the full child `Product` once one
 * is picked — the product grid only holds parents, so a picked variant has to
 * be resolved from the children loaded for the picker (or fetched).
 */

export interface PickerVariants {
  attributes: VariantAttribute[];
  variants: VariantOption[];
  /** Full child rows, keyed by id, so a picked variant can be added to the cart as itself. */
  children: Map<string, Product>;
}

export function mapVariantsResponse(res: ProductVariantsResponse): PickerVariants {
  const variants: VariantOption[] = res.variants.map((v) => ({
    id: v.id,
    sku: v.sku,
    price: Number(v.price),
    stock: Number(v.stock_quantity),
    // The API already resolved the fallback: a variant with no picture of its
    // own carries the parent product's image here.
    imageUrl: v.image_url ?? res.parent?.image_url ?? undefined,
    attributes: Object.fromEntries(
      Object.entries(v.variant_attributes ?? {}).map(([k, val]) => [k, String(val)]),
    ),
  }));
  return {
    attributes: res.attributes.map((a) => ({ name: a.name, values: [...a.values] })),
    variants,
    children: new Map(res.variants.map((v) => [v.id, v])),
  };
}

/**
 * The product to add for a tapped grid tile: a parent from the loaded grid, or
 * a variant child from whatever pickers have loaded so far. `undefined` means
 * the caller has to fetch it — never silently drop the add.
 */
export function resolveSelectedProduct(
  id: string,
  products: readonly Product[],
  loadedVariants: ReadonlyMap<string, Product>,
): Product | undefined {
  return products.find((p) => p.id === id) ?? loadedVariants.get(id);
}
